import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Dialog, DialogContent, Box, Typography, Table, TableBody, TableRow, TableCell, TableHead, Stack, Button, CircularProgress, Alert } from '@mui/material'
import { Order, TransactionType } from '../../proto/generated/order'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { getOrderHistory, OrderHistoryResponse } from '../../services/apis/orders'
import { toLocaleStringIn } from '../../util/general'
import { exchangeToJSON } from '../../proto/generated/common'
import useStyles from './utils/dialogStyles'

interface OrderHistoryDialogProps {
    open: boolean
    onClose: () => void
    onBack: () => void
    order: Order | null
    accountId: string
}

const OrderHistoryDialog: React.FC<OrderHistoryDialogProps> = ({ open, onClose, onBack, order, accountId }) => {
    const { classes } = useStyles()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [historyData, setHistoryData] = useState<OrderHistoryResponse | null>(null)

    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>> | null>(null)
    useEffect(() => {
        if (!order?.security) {
            setSecurity(null)
            return
        }
        const securityKey = getSecurityKey(order.security.exchange ?? 0, order.security.code ?? '')
        getSecurityAsync(securityKey).then(setSecurity)
    }, [order?.security])

    const fetchOrderHistory = useCallback(async () => {
        if (!accountId) return
        setLoading(true)
        setError(null)
        try {
            const data = await getOrderHistory(accountId, order?.brokerOrderId)
            setHistoryData(data)
        } catch (err: any) {
            setError(err.message || 'Failed to fetch order history')
        } finally {
            setLoading(false)
        }
    }, [accountId, order?.brokerOrderId])

    useEffect(() => {
        if (open && (order?.brokerOrderId || order?.orderId)) {
            fetchOrderHistory()
        }
    }, [open, order?.brokerOrderId, order?.orderId, fetchOrderHistory])

    const formatTimestamp = (timestamp: number | string) => {
        if (!timestamp || timestamp === 0) return '—'

        let date: Date

        if (typeof timestamp === 'string') {
            if (timestamp.includes('-') && timestamp.includes(' ')) {
                date = new Date(timestamp)
            } else {
                const num = parseInt(timestamp)
                const adjusted = num < 1e10 ? num * 1000 : num
                date = new Date(adjusted)
            }
        } else {
            const adjusted = timestamp < 1e10 ? timestamp * 1000 : timestamp
            date = new Date(adjusted)
        }

        if (isNaN(date.getTime())) return '—'

        return date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
    }



    if (!order) return null

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: classes.dialog,
                sx: {
                    minHeight: 400,
                    maxHeight: 500,
                    maxWidth: 900
                }
            }}
        >
            <Box className={classes.header}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                            className={`${classes.transactionBadge} ${order.transactionType === TransactionType.BUY ? 'green-text' : 'red-text'}`}
                            sx={{
                                backgroundColor: order.transactionType === TransactionType.BUY ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'
                            }}
                        >
                            {order.transactionType === TransactionType.BUY ? 'BUY' : 'SELL'}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {security?.symbol || order.security?.code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {order.security?.exchange !== undefined ? exchangeToJSON(order.security.exchange) : ''}
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                        #{order.brokerOrderId || order.orderId}
                    </Typography>
                </Stack>
            </Box>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 300 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flex: 1, alignItems: 'center' }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 2, flex: 1 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                ) : (
                    <Box sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid', borderColor: 'divider' } }}>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        bgcolor: 'action.hover',
                                        '& .MuiTableCell-head': {
                                            fontSize: 11,
                                            fontWeight: 500,
                                            color: 'text.secondary',
                                            textTransform: 'uppercase',
                                            py: 1.5,
                                            px: 2,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider'
                                        }
                                    }}
                                >
                                    <TableCell align="center">Time</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Exch. time</TableCell>
                                    <TableCell>Exch. update</TableCell>
                                    <TableCell align="right">Qty.</TableCell>
                                    <TableCell align="right">Filled qty.</TableCell>
                                    <TableCell align="right">Avg. price</TableCell>
                                    <TableCell align="center">Price</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyData?.orders?.map((historyOrder, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            },
                                            '& .MuiTableCell-root': {
                                                py: 1.5,
                                                px: 3,
                                                fontSize: 13
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ color: 'text.primary' }}>
                                            {formatTimestamp((historyOrder as any).order_timestamp || historyOrder.created || historyOrder.exchangeTimestamp || 0)}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.primary' }}>{historyOrder.status}</TableCell>
                                        <TableCell sx={{ color: 'text.primary' }}>{formatTimestamp((historyOrder as any).exchange_timestamp || historyOrder.exchangeTimestamp || 0)}</TableCell>
                                        <TableCell sx={{ color: 'text.primary' }}>
                                            {formatTimestamp((historyOrder as any).exchange_update_timestamp || (historyOrder as any).exchange_timestamp || historyOrder.exchangeTimestamp || 0)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'text.primary' }}>
                                            {historyOrder.quantity || 0}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'text.primary' }}>
                                            {(historyOrder as any).filled_quantity || historyOrder.filledQuantity || 0}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'text.primary' }}>
                                            {(historyOrder as any).average_price
                                                ? toLocaleStringIn((historyOrder as any).average_price / (security?.priceMultiplier ?? 100))
                                                : historyOrder.averageTradedPrice
                                                  ? toLocaleStringIn(historyOrder.averageTradedPrice / (security?.priceMultiplier ?? 100))
                                                  : '0'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'text.primary' }}>
                                            {historyOrder.price ? toLocaleStringIn(historyOrder.price / (security?.priceMultiplier ?? 100)) : '0'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!historyData?.orders || historyData.orders.length === 0) && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                                            No order history available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                <Box className={classes.footer} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: 'auto', py: 1.5 }}>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onBack}
                            className={classes.button}
                            sx={{ minWidth: 80 }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onClose}
                            className={classes.button}
                            sx={{ minWidth: 80 }}
                        >
                            Close
                        </Button>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default OrderHistoryDialog
